import { useState, useRef, useCallback } from 'react'
import { Upload, FileText, CheckCircle2, XCircle, RefreshCw, User, ChevronDown, AlertTriangle, Trash2 } from 'lucide-react'
import api from '../lib/api'

interface Employee { id: string; firstName: string; lastName: string }

interface AnalysisResult {
  fileName: string
  folderName?: string
  parsedName?: string | null
  period?: string | null
  periodLabel?: string | null
  fileSize: number
  fileData: string
  matchedEmployee: Employee | null
  confidence: number
}

export default function PaySlips() {
  const [step, setStep] = useState<'upload' | 'confirm' | 'done'>('upload')
  const [dragging, setDragging] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [results, setResults] = useState<AnalysisResult[]>([])
  const [overrides, setOverrides] = useState<Record<number, string>>({})
  const [employees, setEmployees] = useState<Employee[]>([])
  const [savedCount, setSavedCount] = useState(0)
  const [deleting, setDeleting] = useState(false)
  const [year, setYear] = useState(() => String(new Date().getFullYear()))
  const fileInputRef = useRef<HTMLInputElement>(null)

  const loadEmployees = useCallback(async () => {
    const r = await api.get('/employees')
    setEmployees(r.data || [])
  }, [])

  const analyze = async (files: FileList) => {
    if (!files.length) return
    setAnalyzing(true)
    await loadEmployees()
    const form = new FormData()
    form.append('file', files[0])
    form.append('year', year)

    try {
      const r = await api.post('/documents/payslips/analyze-zip', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setResults(r.data.results)
      setOverrides({})
      setStep('confirm')
    } catch (e: any) {
      alert('Erreur lors de l\'analyse : ' + (e.response?.data?.error || e.message))
    } finally { setAnalyzing(false) }
  }

  const save = async () => {
    setSaving(true)
    try {
      const assignments = results.map((r, i) => {
        const empId = getEmpId(i)
        return {
          employeeId: empId === 'SKIP' || !empId ? null : empId,
          fileName: r.fileName,
          fileData: r.fileData,
          fileSize: r.fileSize,
          period: r.period || null,
          periodLabel: r.periodLabel || null,
        }
      }).filter(a => a.employeeId)

      const r = await api.post('/documents/payslips/save', { assignments })
      setSavedCount(r.data.created)
      setStep('done')
    } catch (e: any) {
      alert('Erreur lors de la sauvegarde : ' + (e.response?.data?.error || e.message))
    } finally { setSaving(false) }
  }

  const deleteAll = async () => {
    if (!confirm('Supprimer TOUS les bulletins de paie importés ? Cette action est irréversible.')) return
    setDeleting(true)
    try {
      const r = await api.delete('/documents/payslips/all')
      alert(`${r.data.deleted} bulletin(s) supprimé(s).`)
    } catch (e: any) {
      alert('Erreur : ' + (e.response?.data?.error || e.message))
    } finally { setDeleting(false) }
  }

  const getEmpId = (i: number) => {
    if (overrides[i] !== undefined) return overrides[i]
    return results[i]?.matchedEmployee?.id ?? ''
  }

  const confidenceColor = (c: number) =>
    c >= 80 ? 'text-green-600 bg-green-50' : c >= 40 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50'
  const confidenceLabel = (c: number) =>
    c >= 80 ? 'Correspondance sûre' : c >= 40 ? 'Correspondance partielle' : 'Non identifié'

  const assignedCount = results.filter((_, i) => { const id = getEmpId(i); return id && id !== 'SKIP' }).length
  const skippedCount = results.filter((_, i) => getEmpId(i) === 'SKIP').length

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="text-blue-600" size={24} /> Bulletins de paie
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Importez le ZIP depuis deskrh.fr — l'année et le mois sont détectés automatiquement.
          </p>
        </div>
        <button
          onClick={deleteAll}
          disabled={deleting}
          className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 border border-red-200 hover:border-red-400 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
          {deleting ? <RefreshCw size={12} className="animate-spin" /> : <Trash2 size={12} />}
          Tout supprimer
        </button>
      </div>

      {/* Étape 1 — Upload */}
      {step === 'upload' && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); analyze(e.dataTransfer.files) }}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-16 text-center cursor-pointer transition-colors ${
            dragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
          }`}>
          <input ref={fileInputRef} type="file" accept=".zip" className="hidden"
            onChange={e => e.target.files && analyze(e.target.files)} />
          {!analyzing && (
            <div className="mb-6" onClick={e => e.stopPropagation()}>
              <label className="text-sm font-semibold text-gray-700 block mb-2">Année des bulletins</label>
              <select value={year} onChange={e => setYear(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white">
                {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          )}
          {analyzing ? (
            <div>
              <RefreshCw size={40} className="mx-auto mb-3 text-blue-500 animate-spin" />
              <p className="font-semibold text-gray-700">Analyse en cours...</p>
              <p className="text-sm text-gray-400 mt-1">Extraction et reconnaissance des employés</p>
            </div>
          ) : (
            <div>
              <Upload size={40} className="mx-auto mb-3 text-gray-400" />
              <p className="font-semibold text-gray-700">Glisser-déposer le fichier ZIP ici</p>
              <p className="text-sm text-gray-500 mt-2">ZIP exporté depuis deskrh.fr · Sélectionner l'année ci-dessus</p>
              <p className="text-xs text-amber-600 mt-3 font-medium">⚠️ RAR non supporté — convertir en ZIP via WinRAR ou 7-Zip</p>
            </div>
          )}
        </div>
      )}

      {/* Étape 2 — Confirmation */}
      {step === 'confirm' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border p-4 flex items-center justify-between">
            <div>
              <p className="font-semibold text-gray-900">{results.length} fichier(s) analysé(s)</p>
              <p className="text-sm text-gray-500">
                <span className="text-green-600 font-medium">{assignedCount} à publier</span>
                {skippedCount > 0 && <> · <span className="text-gray-400">{skippedCount} ignoré(s)</span></>}
              </p>
            </div>
            <button onClick={() => { setStep('upload'); setResults([]) }}
              className="text-sm text-gray-500 hover:text-gray-700 underline">Recommencer</button>
          </div>

          {results.map((r, i) => {
            const empId = getEmpId(i)
            const isSkipped = empId === 'SKIP'
            const isSure = r.confidence >= 80
            const emp = employees.find(e => e.id === empId)

            return (
              <div key={i} className={`bg-white rounded-xl border overflow-hidden ${isSkipped ? 'opacity-50' : ''}`}>
                <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText size={16} className="text-red-500 flex-shrink-0" />
                    <div className="min-w-0">
                      {r.periodLabel && (
                        <span className="inline-block text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-semibold mb-1">{r.periodLabel}</span>
                      )}
                      <span className="text-sm font-medium text-gray-700 truncate block">{r.parsedName || r.folderName}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${confidenceColor(r.confidence)}`}>
                      {confidenceLabel(r.confidence)}
                    </span>
                    <button
                      onClick={() => setOverrides(prev => ({ ...prev, [i]: isSkipped ? (r.matchedEmployee?.id ?? '') : 'SKIP' }))}
                      title={isSkipped ? 'Restaurer' : 'Ignorer'}
                      className={`p-1.5 rounded-lg transition-colors ${isSkipped ? 'text-blue-500 hover:bg-blue-50' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}>
                      {isSkipped ? <RefreshCw size={14} /> : <Trash2 size={14} />}
                    </button>
                  </div>
                </div>

                {!isSkipped && (
                  <div className="px-4 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <User size={16} className="text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-400 mb-1">Assigné à</p>
                      {isSure && emp && overrides[i] !== '__EDIT__' ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-800">{emp.firstName} {emp.lastName}</span>
                          <button onClick={() => setOverrides(prev => ({ ...prev, [i]: '__EDIT__' }))}
                            className="text-xs text-blue-500 hover:underline">modifier</button>
                        </div>
                      ) : (
                        <div className="relative">
                          <select
                            value={empId === '__EDIT__' ? '' : empId}
                            onChange={e => setOverrides(prev => ({ ...prev, [i]: e.target.value }))}
                            className={`w-full border rounded-lg px-3 py-2 text-sm appearance-none pr-8 focus:ring-2 focus:ring-blue-500 outline-none ${
                              !empId || empId === '__EDIT__' ? 'border-red-300 bg-red-50' : 'border-gray-200'
                            }`}>
                            <option value="">— Sélectionner un employé —</option>
                            {employees.map(e => (
                              <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>
                            ))}
                          </select>
                          <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        </div>
                      )}
                    </div>
                    {(empId && empId !== 'SKIP' && empId !== '__EDIT__')
                      ? <CheckCircle2 size={20} className="text-green-500 flex-shrink-0" />
                      : <AlertTriangle size={20} className="text-red-400 flex-shrink-0" />
                    }
                  </div>
                )}

                {isSkipped && (
                  <div className="px-4 py-2 flex items-center gap-2 text-gray-400">
                    <XCircle size={16} />
                    <span className="text-sm">Ignoré — employé inconnu ou inactif</span>
                  </div>
                )}
              </div>
            )
          })}

          <button
            onClick={save}
            disabled={saving || assignedCount === 0}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
            {saving
              ? <><RefreshCw size={18} className="animate-spin" /> Envoi en cours...</>
              : <><CheckCircle2 size={18} /> Publier {assignedCount} bulletin(s) aux employés</>
            }
          </button>
        </div>
      )}

      {/* Étape 3 — Succès */}
      {step === 'done' && (
        <div className="text-center py-16">
          <CheckCircle2 size={64} className="mx-auto mb-4 text-green-500" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{savedCount} bulletin(s) publiés !</h2>
          <p className="text-gray-500 mb-6">Les employés peuvent consulter leur bulletin depuis l'app mobile.</p>
          <button onClick={() => { setStep('upload'); setResults([]); setSavedCount(0) }}
            className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold">
            Importer une autre période
          </button>
        </div>
      )}
    </div>
  )
}
